import { useState, useCallback } from 'react'
import { WorkSheetProductStatusURLs } from '../types'
import { ButtonRed, ButtonPrimary } from '../../../../../../../baseComponents/Button'
import { ModalSmallCentered } from '../../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { RowData } from '../../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { orderSortFunction } from '../../../../../../../forms/CustomSelect/functions'
import { InputNumberWithLabelMargin } from '../../../../../../../forms/Input'
import twinFetchPost from '../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { SetEditDeleteModalTy, UseEditDeleteModalProps } from '../../../../../../../utils/hooks/useEditDeleteModal'
import { CustomChangeFunction } from '../../../../../../../forms/Input/types'

interface ModalEditProductStatusViewProps extends ModalEditProductStatusViewLogicProps { }

const ModalEditProductStatusView: React.FC<ModalEditProductStatusViewProps> = ({ allRowData, ...rest }) => {
    const { productQuantity,  handleOnSubmit, setProductQuantity } = useModalEditProductStatusViewLogic({ allRowData, ...rest })
    return (
        <ModalSmallCentered opened={true} setOpened={rest.setOpened}>
            <h2><TwinTrans transKey='dividedProductStatus'>Dividir el estado del producto</TwinTrans></h2>
            <div className='flex flex-auto flex-col items-start my-15 mt-30'>
                <InputStatusProduct {...{ memorizedGroup: rest.memorizedGroup, setProductQuantity, productQuantity, maxValue: parseFloat(allRowData?.product?.quantity) }} />
            </div>
            <div className='flex items-center justify-center gap-20'>
                <ButtonRed onClick={() => {rest.setOpened(null)}} disabled={false}>
                    <TwinTrans transKey='cancel'>Cancelar</TwinTrans>
                </ButtonRed>
                <ButtonPrimary onClick={handleOnSubmit} disabled={false}>
                    <TwinTrans transKey='split'>Dividir</TwinTrans>
                </ButtonPrimary>
            </div>
        </ModalSmallCentered>
    )
}

interface ProductQuantityTy {
    quantity?: string
    WorkSheetProductStatusId?: number
    typeStatus?: number
    alreadyOrderProcessed?: number
}

interface ModalEditProductStatusViewLogicProps extends ModalEditComponentProps {
    allRowData?: RowData
    memorizedGroup: TwinDictionary
    workSheetProductStatusURLs: WorkSheetProductStatusURLs
    getProducts: () => Promise<void>
    setOpenModal: SetEditDeleteModalTy
}

const useModalEditProductStatusViewLogic = ({ allRowData, setOpened, memorizedGroup, workSheetProductStatusURLs, getProducts, setOpenModal }: ModalEditProductStatusViewLogicProps) => {
    const orderStatus = memorizedGroup?.WorkSheetProductStatusesDict[allRowData?.product?.WorkSheetProductStatusId || allRowData?.WorkSheetProductStatusId].order
    const nextStatus = memorizedGroup?.WorkSheetProductStatuses[orderStatus]?.id
    const [productQuantity, setProductQuantity] = useState<ProductQuantityTy>({
        quantity: allRowData?.product?.quantity,
        WorkSheetProductStatusId: nextStatus || allRowData?.product?.WorkSheetProductStatusId || allRowData?.WorkSheetProductStatusId,
        alreadyOrderProcessed: allRowData?.product?.alreadyOrderProcessed
    })

    const handleProductOnSubmit = useCallback(async (skipMatchCheck?: boolean) => {
        const result = await twinFetchPost(workSheetProductStatusURLs.handleProductStatus, { WorkSheetId: allRowData?.product.WorkSheetId, WorkSheetProductId: allRowData?.product.id, productQuantity, skipMatchCheck })
        const responseJSON = await result.json()
        if (skipMatchCheck) {
            setOpened(null)
        }
        return responseJSON
    }, [allRowData?.product.WorkSheetId, allRowData?.product.id, productQuantity, workSheetProductStatusURLs.handleProductStatus, setOpened])

    const handleOnSubmit = useCallback(async () => {
        const typeStatus = productQuantity?.WorkSheetProductStatusId ? memorizedGroup?.WorkSheetProductStatusesDict?.[productQuantity?.WorkSheetProductStatusId]?.typeStatus : null
        productQuantity.typeStatus = typeStatus
        const result = await handleProductOnSubmit()
        const LabelId = productQuantity?.WorkSheetProductStatusId && memorizedGroup?.WorkSheetProductStatusesDict?.[productQuantity?.WorkSheetProductStatusId]?.LabelId
        const SpecificPriceRuleForLabelId = productQuantity?.WorkSheetProductStatusId && memorizedGroup?.WorkSheetProductStatusesDict?.[productQuantity?.WorkSheetProductStatusId]?.SpecificPriceRuleForLabelId
        let openModal: UseEditDeleteModalProps = null
        if (result?.errors && result?.errors?.[0].msg.includes('matchProduct')) {
            const errorSplit = result.errors[0].msg.split('-_-')
            const WorkSheetProductMatchId = errorSplit[1]
            const productReference = errorSplit[3]
            openModal = {
                type: 'edit',
                allRowData: { WorkSheetProductId: allRowData?.product?.id, WorkSheetProductMatchId, productReference }
            }
        }
        if (LabelId) {
            let extraParamsForTag: TwinDictionary = { id: allRowData?.product?.WorkSheetId, tagQuantity: productQuantity.quantity, LabelId, SpecificPriceRuleId: SpecificPriceRuleForLabelId, WorkSheetProductId: allRowData?.product?.id}
            if (openModal?.allRowData) {
                extraParamsForTag['extraParamsForMatch'] = openModal?.allRowData
            }
            openModal = { type: 'delete', allRowData: extraParamsForTag }
        }
        if (openModal) {
            setOpenModal(openModal)
        } else {
            getProducts()
            setOpened(null)
        }
        return result
    }, [productQuantity, allRowData, setOpened, getProducts, memorizedGroup, setOpenModal, handleProductOnSubmit])

    return { productQuantity, setProductQuantity, handleOnSubmit, handleProductOnSubmit }
}

interface InputStatusProductProps extends InputStatusProductLogicProps {
    productQuantity: ProductQuantityTy
    memorizedGroup: TwinDictionary
}

const InputStatusProduct: React.FC<InputStatusProductProps> = ({  memorizedGroup, productQuantity, maxValue, setProductQuantity }) => {
    const { t } = useTwinTranslation()
    const { onBlur, updateProductQuantity, updateProductStatus} = useInputStatusProductLogic({maxValue, setProductQuantity})
    return (
        <div className='flex justify-start items-center w-full mb-14'>
            <InputNumberWithLabelMargin className={'w-100x mt-auto text-left '} label={t('quantity', 'Cantidad')} value={String(productQuantity?.quantity)} onChange={updateProductQuantity} maxValue={maxValue} onBlur={onBlur} />
            <div className={'w-full ml-40 text-left'}>
                <CustomSelectWithSearchBar label={t('status', 'Estado')} items={memorizedGroup?.WorkSheetProductStatusesDict || {}} value={productQuantity.WorkSheetProductStatusId} onChange={updateProductStatus} sortFunction={orderSortFunction} />
            </div>
        </div>
    )
}
interface InputStatusProductLogicProps {
    maxValue: number
    setProductQuantity: React.Dispatch<React.SetStateAction<ProductQuantityTy>>
}

const useInputStatusProductLogic = ({ maxValue, setProductQuantity }: InputStatusProductLogicProps) => {

    const updateProductQuantity: CustomChangeFunction = useCallback(async (value) => {
        await setProductQuantity((old) => {
            const oldCopy = JSON.parse(JSON.stringify(old))
            return { ...oldCopy, quantity: value }
        })
    }, [setProductQuantity])

    const updateProductStatus: CustomChangeFunction = useCallback(async (value) => {
        await setProductQuantity((old) => {
            const oldCopy = JSON.parse(JSON.stringify(old))
            return { ...oldCopy, WorkSheetProductStatusId: parseInt(value) }
        })
    }, [setProductQuantity])

    const onBlur = useCallback((e: any) => {
        const value = e.currentTarget.value
        if (!value || !parseFloat(value)) {
            updateProductQuantity(String(maxValue))
        }
    }, [maxValue, updateProductQuantity])

    return {updateProductQuantity, updateProductStatus, onBlur}
}

export default ModalEditProductStatusView